import { Select } from '@mantine/core';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { IconName } from 'shared/components/Icon';
import SendingMedia from 'shared/components/SendingMedium/SendingMedia';
import FormSection from 'shared/forms/FormSection';
import { Button, ButtonColor } from 'shared/io';
import { treatTemplate } from 'shared/serializer';
import { settingsActions } from 'store/settings/settings.actions';
import type { RecoverActionType, RecoveryPlanStep } from 'types/recovery-plan';
import type { StoreState } from 'types/storeTypes';
import type { Template } from 'types/template';

import styleIdentifiers from './RecoveryStepForm.module.scss';

const styles = classNames.bind(styleIdentifiers);

type RecoveryStepFormSendReminderProps = {
  availableTemplateTypes: RecoverActionType[];
};

export const RecoveryStepFormSendReminder = ({
  availableTemplateTypes,
  children,
}: PropsWithChildren<RecoveryStepFormSendReminderProps>) => {
  const { t } = useTranslation();

  const {
    sending_medium: sendingMedium = [],
    recovery_step_types: { recovery_plan_steps: templateTypes = [] },
  } = useSelector((state: StoreState) => state.app.constants);

  const [templateList, setTemplateList] = useState<Template[]>([]);
  const [selectedReminderType, setSelectedReminderType] = useState<RecoverActionType>();

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<RecoveryPlanStep>();

  register('step_type', { required: true });
  register('template', { required: true });
  register('template_id', { required: true });

  const template = watch('template');
  const sendingMedia = watch('sending_media') || [];

  const getTemplates = useCallback((stepType: RecoverActionType) => {
    settingsActions.templateList({
      data: {
        step_type: stepType,
      },
      noLoading: true,
      callback: ({ data }: any) => {
        setTemplateList(data.map((item: any) => treatTemplate(item)));
      },
    });
  }, []);

  const setTemplate = (newTemplate?: Template) => {
    setValue('template', newTemplate as any);
    setValue('template_id', newTemplate?.id as any);
    setValue('step_type', newTemplate?.step_type as any);
  };

  const addMedium = () => {
    const defaultSendingMedium = (sendingMedium.filter(
      e => !sendingMedia.some(el => e.value === el.name),
    ) || sendingMedium)[0];

    if (sendingMedia.length < sendingMedium.length) {
      setValue(
        'sending_media',
        [
          ...sendingMedia,
          {
            name: defaultSendingMedium.value,
            order: sendingMedia.length + 1,
            force_sending: 'true',
          },
        ],
        {
          shouldTouch: true,
        },
      );
    }
  };

  const filteredTemplateTypes = useMemo(
    () => templateTypes.filter(({ value }) => availableTemplateTypes.includes(value)),
    [templateTypes, availableTemplateTypes],
  );

  useEffect(() => {
    if (template) {
      setValue('template_id', template.id);
      setSelectedReminderType(template.step_type);
      getTemplates(template.step_type);
    } else if (!selectedReminderType) {
      setSelectedReminderType(filteredTemplateTypes[0]?.value);
      getTemplates(filteredTemplateTypes[0]?.value);
    }
  }, [template, setValue, selectedReminderType, filteredTemplateTypes, getTemplates]);

  return (
    <>
      <FormSection title={t(i18nKeys.DEBTOR.SENDING_MEDIUMS)}>
        <SendingMedia fieldName="sending_media" className={styles('input')} />
        {sendingMedia.length < sendingMedium.length && (
          <Button
            color={ButtonColor.GREY}
            label={t(i18nKeys.ACTIONS.ADD)}
            iconRight={IconName.PLUS}
            onClick={addMedium}
            small
          />
        )}
      </FormSection>
      {children}
      <FormSection title={t(i18nKeys.COMMON.TEMPLATE)} className={styles('form-section')}>
        <div className={styles('actions')}>
          {errors.template && (
            <div className={styles('error-message', 'error')}>
              {t(i18nKeys.PLAN.SELECT_TEMPLATE)}
            </div>
          )}
          <Select
            label={t(i18nKeys.TEMPLATE_TYPE)}
            onChange={value => {
              const selectedType = value as RecoverActionType;
              setTemplate();
              setSelectedReminderType(selectedType);
              getTemplates(selectedType);
            }}
            allowDeselect={false}
            disabled={filteredTemplateTypes.length === 1}
            value={selectedReminderType}
            data={filteredTemplateTypes.map(({ description, value }) => ({
              label: description,
              value,
            }))}
          />
          <Select
            label={t(i18nKeys.TEMPLATE.TEMPLATE)}
            onChange={value => {
              const selectedTemplate = templateList.find(item => String(item.id) === value);
              setTemplate(selectedTemplate);
            }}
            clearable
            nothingFoundMessage={t(i18nKeys.NO_RESULT)}
            value={template ? String(template.id) : null}
            data={templateList.map(item => ({
              label: item.name,
              value: String(item.id),
            }))}
          />
        </div>
      </FormSection>
    </>
  );
};
